import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Modal, ModalActions, ModalActionType, ModalService } from 'weavix-shared/services/modal.service';
import { debounce } from 'lodash';
import { AlertService, ServiceError } from 'weavix-shared/services/alert.service';
import { CompanyService } from 'weavix-shared/services/company.service';
import { TranslationService } from 'weavix-shared/services/translation.service';
import { WaltService } from 'weavix-shared/services/walt.service';
import { isVersionNewer } from '@weavix/domain/src/utils/is-version-newer';
import { FEATURE_ICONS } from 'weavix-shared/utils/feature.icons';
import { AutoUnsubscribe, getBatteryIcon } from 'weavix-shared/utils/utils';
import { FacilityService } from 'weavix-shared/services/facility.service';
import { ProfileService } from 'weavix-shared/services/profile.service';
import { PermissionAction } from '@weavix/models/src/permission/permissions.model';
import { Facility } from 'weavix-shared/models/facility.model';
import { DropdownItem } from 'components/dropdown/dropdown.model';
import { LoadingComponent } from 'components/loading/loading.component';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from 'components/icon/icon.component';
import { CommonModule } from '@angular/common';
import { DropdownComponent } from 'components/dropdown/dropdown.component';
import { FormsModule } from '@angular/forms';
import { AvatarComponent } from 'components/avatar/avatar.component';
import { ModalComponent } from 'components/modal/modal.component';
import { SegmentedToggleComponent, SegmentedToggleItem } from 'components/segmented-toggle/segmented-toggle.component';
import { TimeAgoPipe } from 'weavix-shared/pipes/time-ago.pipe';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { Geofence, MapWalt } from 'weavix-shared/models/weavix-map.model';
import { Point } from 'weavix-shared/models/location.model';
import { MapUtils } from 'weavix-shared/utils/map';
import { getWaltModel } from '@weavix/models/src/walt/walt';
import { AccountService } from 'weavix-shared/services/account.service';
import { FeatureFlagService } from 'weavix-shared/services/feature-flag.service';
import { FeatureId } from '@weavix/models/src/features/feature';
import { CraftService } from 'weavix-shared/services/craft.service';
import { WaltStatus, WaltStatusTranslation } from 'weavix-shared/models/walt.model';

import { GeofenceService } from 'weavix-shared/services/geofence.service';
import { FloorPlanService } from 'weavix-shared/services/floor-plan.service';
import { NewRelicService } from 'weavix-shared/services/new-relic.service';

import { WaltDetailHistoryComponent } from './walt-detail-history/walt-detail-history.component';
import { NewDetailsComponent } from './new-details/new-details.component';
import { WaltSoundControlComponent } from './walt-sound-control/walt-sound-control.component';
import { GeofenceAreaInfo, getAllowedFacilityIds, isOnKnownSite, formatCoordinates, getMapLocation, polygonArea, selectDisplayGeofence, selectPinGeofence } from './walt-location.utils';
import { WaltCommand, WaltCommandConfirmModalComponent } from './walt-command-confirm-modal/walt-command-confirm-modal.component';
import { WaltRefreshService } from './walt-refresh.service';

type WaltDetailTab = 'details' | 'history';

/** A repeater site's geofences and the level of each of its floor plans, keyed by floor plan id. */
interface RepeaterSite {
    geofences: Geofence[];
    floorPlanLevels: ReadonlyMap<string, number>;
}

/** A walt command waiting on the confirmation modal, with what to send and toast once confirmed. */
interface PendingWaltCommand {
    command: WaltCommand;
    send: () => Promise<void>;
    messageKey: string;
}

const EMPTY_REPEATER_SITE: RepeaterSite = { geofences: [], floorPlanLevels: new Map() };

@AutoUnsubscribe()
@Component({
    selector: 'app-walt-detail',
    templateUrl: './walt-detail.component.html',
    styleUrls: ['./walt-detail.component.scss', '../map-detail-view.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,

        AvatarComponent,
        IconComponent,
        DropdownComponent,
        LoadingComponent,
        ModalComponent,
        SegmentedToggleComponent,
        TimeAgoPipe,
        WaltDetailHistoryComponent,
        NewDetailsComponent,
        WaltSoundControlComponent,
        WaltCommandConfirmModalComponent,
    ],
    providers: [WaltRefreshService],
})
export class WaltDetailComponent {
    private readonly alertService = inject(AlertService);
    private readonly accountService = inject(AccountService);
    private readonly companyService = inject(CompanyService);
    private readonly craftService = inject(CraftService);
    private readonly waltService = inject(WaltService);
    private readonly modalService = inject(ModalService);
    private readonly translationService = inject(TranslationService);
    private readonly facilityService = inject(FacilityService);
    private readonly profileService = inject(ProfileService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly geofenceService = inject(GeofenceService);
    private readonly floorPlanService = inject(FloorPlanService);
    private readonly waltRefresh = inject(WaltRefreshService);

    readonly walt = input.required<MapWalt>();
    readonly closeOutput = output<void>();
    /** Emits the walt record from a completed refresh session, for the parent to apply to the shared store. */
    readonly refreshedOutput = output<MapWalt>();
    /** Emits the point to zoom the map to when the sidebar location is clicked. */
    readonly locateOutput = output<Point>();

    readonly shouldShowWaltModel = this.accountService.getAccountWaltType() === 'both';
    readonly waltModel = computed(() => getWaltModel(this.walt().id));
    readonly waltModelTranslation = computed(() => this.translationService.getImmediate(`walt.${this.waltModel()}`));
    readonly loadingCount = signal(0);
    readonly waltIcon = FEATURE_ICONS.radio.icon;
    readonly showOnSite = toSignal(this.featureFlagService.getFeatureFlag$(FeatureId.WaltCredentialEnforced));
    readonly deviceManagementEnhancementsEnabled = toSignal(this.featureFlagService.getFeatureFlag$(FeatureId.Walt2DeviceManagementEnhancements));
    readonly batteryIcon = computed(() => getBatteryIcon(this.walt().battery * 100));
    readonly waltStatus = computed(() =>
        WaltService.getWaltStatus(this.walt(), this.deviceManagementEnhancementsEnabled()),
    );
    readonly waltStatusLabel = computed(() =>
        this.translationService.getImmediate(WaltStatusTranslation[this.waltStatus()]),
    );
    /** True while a refresh of this walt is running. */
    readonly isRefreshingInfoBoxes = this.waltRefresh.isRefreshing;
    readonly tab = signal<WaltDetailTab>('details');
    readonly tabs: ReadonlyArray<SegmentedToggleItem<WaltDetailTab>> = [
        { id: 'details', labelKey: 'devices.details' },
        { id: 'history', labelKey: 'devices.history.tab-title' },
    ];

    private readonly walt$ = toObservable(this.walt);
    readonly company = toSignal(this.walt$.pipe(
        map(walt => walt.lastAssigned?.companyId),
        distinctUntilChanged(),
        tap(() => this.addLoading()),
        switchMap(async companyId => {
            if (!companyId) return null;
            else return await this.companyService.get(this, companyId);
        }),
        catchError((e: any) => {
            this.alertService.sendServiceError(e, ServiceError.Get, 'walt.walt');
            return of(null);
        }),
        tap(() => this.removeLoading()),
    ));
    readonly lastAssigned = computed(() => this.waltService.getWaltLastAssignedName(this.walt()));
    readonly person = toSignal(this.walt$.pipe(
        distinctUntilChanged((prev, curr) => {
            return prev.person === curr.person
                && prev.lastAssigned?.personId === curr.lastAssigned?.personId
                && prev.wrangler?.id === curr.wrangler?.id
                && prev.wrangler?.date === curr.wrangler?.date;
        }),
        tap(() => this.addLoading()),
        switchMap(async walt => {
            const currentlyAssigned = WaltService.isCurrentlyAssigned(walt);
            const lastAssignedName = this.waltService.getWaltLastAssignedName(walt);
            // handles edge case where person is fully removed from the database...
            if (currentlyAssigned &&
                lastAssignedName !== this.translationService.getImmediate('generics.unknown')
            ) {
                return await this.waltService.getWaltLastAssignedPerson(this, walt);
            } else {
                return null;
            }
        }),
        catchError((e: any) => {
            this.alertService.sendServiceError(e, ServiceError.Get, 'walt.walt');
            return of(null);
        }),
        tap(() => this.removeLoading()),
    ));
    readonly hasDeviceManagementEditPerms = computed(() => {
        const walt = this.walt();
        return this.profileService.hasFacilitiesPermission(PermissionAction.EditDeviceManagement, walt.facilityId);
    });
    readonly hasDeviceNetworkStatusPerms = computed(() => {
        const walt = this.walt();
        return this.profileService.hasPermission(PermissionAction.EnableDeviceNetworkStatus, walt.facilityId);
    });
    readonly isRepeaterMode = computed(() => {
        const walt = this.walt();
        return !!walt.meshRepeater;
    });
    readonly isOnSite = computed(() => {
        const walt = this.walt();
        return walt.badge?.facilities?.some(f => f.id === walt.facilityId) ?? false;
    });
    private readonly badgeGeofenceIds = computed(() =>
        (this.walt().badge?.geofences ?? []).filter(g => g.id && g.date && g.facilityId).map(g => g.id),
    );
    /**
     * Name and polygon area per badge-reported geofence id, fetched through the cached
     * GeofenceService. Missing entries (still loading, deleted, or failed) make the display fall
     * back to the badge-reported name of the last badge entry; failures are reported to New Relic
     */
    private readonly geofenceAreas = toSignal(toObservable(this.badgeGeofenceIds).pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((id, i) => id === curr[i])),
        switchMap(async ids => {
            const failedIds: string[] = [];
            const entries = await Promise.all(ids.map(async id => {
                try {
                    const geofence = await this.geofenceService.get(this, id);
                    return [id, { name: geofence?.name, area: polygonArea(geofence?.vertices) }] as const;
                } catch {
                    failedIds.push(id);
                    return null;
                }
            }));
            if (failedIds.length) {
                NewRelicService.sendError(new Error(`failed to load geofences for walt ${this.walt().id}: ${failedIds.join(', ')}`));
            }
            return new Map<string, GeofenceAreaInfo>(entries.filter(entry => entry !== null));
        }),
    ), { initialValue: new Map<string, GeofenceAreaInfo>() });
    private readonly allowedFacilityIds = computed(() => getAllowedFacilityIds(this.walt(), this.person()));

    readonly isOnKnownSite = computed(() => isOnKnownSite(this.walt().badge, this.allowedFacilityIds()));

    readonly displayGeofenceName = computed(() => selectDisplayGeofence(
        this.walt().badge,
        this.allowedFacilityIds(),
        this.walt().facilityId,
        this.geofenceAreas(),
    )?.name ?? null);
    private readonly repeaterFacilityId = computed(() =>
        this.deviceManagementEnhancementsEnabled() ? this.walt().meshRepeater?.facilityId : undefined,
    );
    /** Geofences and floor plan levels of the repeater's site, from the cached services; empty on failure (reported to New Relic by walt id only). */
    private readonly repeaterSite = toSignal(toObservable(this.repeaterFacilityId).pipe(
        distinctUntilChanged(),
        switchMap(async (facilityId): Promise<RepeaterSite> => {
            if (!facilityId) return EMPTY_REPEATER_SITE;
            const waltId = this.walt().id;
            try {
                const [geofences, floorPlans] = await Promise.all([
                    this.geofenceService.getAll(this, facilityId),
                    this.floorPlanService.getAll(this, facilityId),
                ]);
                return {
                    geofences: geofences ?? [],
                    floorPlanLevels: new Map((floorPlans ?? []).filter(fp => fp.id).map(fp => [fp.id, fp.level])),
                };
            } catch (e) {
                const attributes: Record<string, string | number> = { waltId };
                if (e instanceof HttpErrorResponse) attributes.status = e.status;
                NewRelicService.sendError(new Error('failed to load geofences or floor plans for repeater walt'), attributes);
                return EMPTY_REPEATER_SITE;
            }
        }),
    ), { initialValue: EMPTY_REPEATER_SITE });

    /** Sidebar location name: for a repeater with a valid pin, the smallest geofence on its floor containing it or its coordinates; otherwise the badge geofence. */
    readonly locationLabel = computed(() => {
        const repeater = this.walt().meshRepeater;
        const pin = repeater?.location;
        if (!this.isRepeaterMode() || !MapUtils.isValidLocation(pin)) return this.displayGeofenceName();
        const site = this.repeaterSite();
        return selectPinGeofence(pin, repeater.level, site.geofences, site.floorPlanLevels)?.name ?? formatCoordinates(pin);
    });
    /** The point the map zooms to for this walt, or null when it has no valid location. */
    readonly mapLocation = computed(() => {
        const location = getMapLocation(this.walt(), this.deviceManagementEnhancementsEnabled());
        return MapUtils.isValidLocation(location) ? location : null;
    });
    readonly canLogOut = computed(() => {
        const walt = this.walt();
        if (!this.hasDeviceManagementEditPerms())
            return false;
        else if (WaltService.isCurrentlyAssigned(walt) && (
            this.deviceManagementEnhancementsEnabled() ?
                (WaltService.getWaltStatus(walt, true) === WaltStatus.Active) :
                (WaltService.waltActive(walt) && !walt.poweredOff)
        ))
            return true;
        else
            return false;
    });
    readonly facilities = toSignal(this.walt$.pipe(
        map(walt => walt.facilityId),
        distinctUntilChanged(),
        tap(() => this.addLoading()),
        switchMap(async facilityId => {
            return (await this.facilityService.getMyFacilitiesOnCurrentAccount(this))
                .filter(x => x.id === facilityId || this.profileService.hasFacilitiesPermission(PermissionAction.EditDeviceManagement, x.id));
        }),
        catchError((e: any) => {
            this.alertService.sendServiceError(e, ServiceError.Get, 'walt.walt');
            return of([] as Facility[]);
        }),
        tap(() => this.removeLoading()),
    ), { initialValue: [] });
    /** True when the walt's assigned site has Dual SIM enabled; false if the site is unknown or the setting is unset. */
    readonly useDualSims = computed(() => this.facilities().find(f => f.id === this.walt().facilityId)?.waltSettings?.useDualSims === true);
    /** Latest global version for the walt's model; null while loading or when it could not be loaded. */
    private readonly latestWaltVersion = toSignal(this.walt$.pipe(
        map(walt => getWaltModel(walt.id)),
        distinctUntilChanged(),
        switchMap(async model => {
            try {
                return await this.waltService.getLatestWaltVersion(model, this);
            } catch (e) {
                NewRelicService.sendError(e instanceof Error ? e : new Error('failed to load the latest walt version'), { waltModel: model });
                return null;
            }
        }),
    ), { initialValue: null });
    /** True when the walt's version is at or ahead of the version it is required to run; false when either is unknown. */
    readonly meetsRequiredVersion = computed(() => {
        const model = this.waltModel();
        const latest = this.latestWaltVersion();
        if (!model || latest === null) return false;
        const required = WaltService.getRequiredWaltVersion(model, { [model]: latest }, this.walt().waltSettings);
        return !!this.walt().version && !!required && !isVersionNewer(this.walt().version, required);
    });
    /** True when the enhancements flag is on for the walt's own site, which the backend requires for Update, Off and Restart. */
    readonly commandsEnabled = toSignal(this.walt$.pipe(
        distinctUntilChanged((prev, curr) => prev.accountId === curr.accountId && prev.facilityId === curr.facilityId),
        switchMap(walt => this.featureFlagService.getFacilityFeatureFlag$(
            FeatureId.Walt2DeviceManagementEnhancements, walt.accountId ?? '', walt.facilityId ? [walt.facilityId] : [])),
    ), { initialValue: false });

    readonly facilityDropdown = computed(() => this.facilities().map(f => {
        return {
            label: f.name,
            key: f.id,
        };
    }));
    private readonly modalActions: ModalActions = {
        cancel: {
            show: true,
            textKey: 'generics.no',
        },
        submit: {
            show: true,
            textKey: 'generics.yes',
            buttonClass: 'blue-lt',
        },
    };
    /** The walt command awaiting typed confirmation, or null when no confirmation is open. */
    readonly pendingCommand = signal<PendingWaltCommand | null>(null);
    readonly waltFacility = signal<string | undefined>(undefined);
    readonly changeFacility = signal<Modal | null>(null);
    readonly craftsDropdown = toSignal(
        this.walt$.pipe(
            map(w => w.facilityId),
            distinctUntilChanged(),
            switchMap(() => this.craftService.getAll(this)),
            map(crafts => (crafts ?? []).filter(c => !c.disabled).map<DropdownItem>(c => ({ key: c.id, label: c.name }))),
            catchError(() => of([] as DropdownItem[])),
        ),
        { initialValue: [] as DropdownItem[] },
    );
    readonly selectedCraft = signal<string | undefined>(undefined);
    /** True while the last refresh failed and the walt has not reported since. */
    readonly refreshFailed = this.waltRefresh.refreshFailed;
    private lastWaltId: string | undefined;

    constructor() {
        this.waltRefresh.init(this, this.walt);
        this.waltRefresh.refreshed$.pipe(takeUntilDestroyed()).subscribe(walt => this.refreshedOutput.emit(walt));

        // TODO(Angular 19): use linkedSignal for waltFacility instead.
        // We want a signal that initializes from walt(), so effect() is the only way pre-Angular 19.
        effect(() => {
            const walt = this.walt();
            this.selectedCraft.set(walt.craft);
            this.waltFacility.set(walt.facilityId);
            if (walt.id !== this.lastWaltId) {
                this.lastWaltId = walt.id;
                this.tab.set('details');
                this.waltRefresh.reset();
            }
        }, { allowSignalWrites: true });
    }

    onTabChange(tab: WaltDetailTab): void {
        this.tab.set(tab);
    }

    private addLoading() {
        this.loadingCount.update(n => n + 1);
    }

    private removeLoading() {
        this.loadingCount.update(n => n - 1);
    }

    onFacilityOutput(selected: DropdownItem) {
        this.waltFacility.set(selected.key);
        const facility = this.facilities().find(f => f.id === selected.key);
        this.changeFacility.set({
            isOpen: true,
            width: 450,
            actionsAlignment: 'right',
            header: {
                textKey: 'walt.site-change.header',
                textAlignment: 'left',
                showSeparator: true,
            },
            actions: {
                [ModalActionType.submit]: {
                    textKey: 'generics.confirm',
                    show: true,
                },
                [ModalActionType.cancel]: {
                    textKey: 'generics.cancel',
                    show: true,
                },
            },
            fullScreen: false,
            content: false,
            textAlignment: 'left',
            padding: 20,
            textKey: 'walt.site-change.body',
            textStyle: {
                'padding-top': '20px',
                'padding-bottom': '10px',
                'font-size': '16px',
            },
            params: { siteName: facility.name },
        });
    }

    /**
     * Saves the confirmed site change. The request is not bound to this sidebar, which closes mid-save when the
     * walt leaves the selected site's table; binding it would cancel the request and leave the app loading forever.
     */
    async updateWaltFacility(action: ModalActionType) {
        this.changeFacility.set(null);
        if (action !== ModalActionType.submit) {
            this.waltFacility.set(this.walt().facilityId);
            return;
        }
        try {
            this.alertService.setAppLoading(true);
            await this.waltService.setWaltFacility(null, this.walt().id, this.waltFacility());
            this.alertService.sendSuccess({ messageKey: 'walt.site-change.success' });
        } catch (e: any) {
            this.waltFacility.set(this.walt().facilityId);
            this.alertService.sendServiceError(e, ServiceError.Update, 'walt.walt');
        } finally {
            this.alertService.setAppLoading(false);
        }
    }

    async onCraftOutput(selected: DropdownItem) {
        const previousCraftId = this.walt().craft;
        const craftId = selected?.key ?? undefined;
        this.selectedCraft.set(craftId);
        try {
            this.alertService.setAppLoading(true);
            await this.waltService.updateWaltCraft(this, this.walt().id, craftId ?? null);
            this.alertService.sendSuccess({ messageKey: 'walt.craft-change.success' });
        } catch (e: any) {
            this.selectedCraft.set(previousCraftId);
            this.alertService.sendServiceError(e, ServiceError.Update, 'walt.walt');
        } finally {
            this.alertService.setAppLoading(false);
        }
    }

    async rebootWalt() {
        await this.sendWaltCommand(() => this.waltService.rebootWalt(this, this.walt().id), 'walt.restart-command.success', 'restart');
    }

    async powerOffWalt() {
        await this.sendWaltCommand(() => this.waltService.powerOffWalt(this, this.walt().id), 'walt.power-off-command.success', 'power-off');
    }

    /** Tells the walt to install its required version; the walt does not confirm, so only the send is reported. */
    async installWaltUpdate() {
        await this.sendWaltCommand(() => this.waltService.installWaltUpdate(this, this.walt().id), 'walt.update-command.success', 'update');
    }

    /** Opens the typed-confirmation modal for `command`; nothing is sent until it is confirmed. */
    private sendWaltCommand(send: () => Promise<void>, messageKey: string, command: WaltCommand) {
        this.pendingCommand.set({ command, send, messageKey });
    }

    /** Clears the pending command without sending anything. */
    closeWaltCommand() {
        this.pendingCommand.set(null);
    }

    /** Closes the confirmation modal, sends the pending command and toasts the result. */
    async confirmWaltCommand() {
        const pending = this.pendingCommand();
        if (!pending) return;
        this.pendingCommand.set(null);
        try {
            this.alertService.setAppLoading(true);
            await pending.send();
            this.alertService.sendSuccess({ messageKey: pending.messageKey });
        } catch (e) {
            this.alertService.sendServiceError(e, ServiceError.Update, 'walt.walt');
        } finally {
            this.alertService.setAppLoading(false);
        }
    }

    private readonly debounceLogout = debounce(async () => await this.waltService.remoteLogout(this, this.walt().id), 500);
    async remoteLogout() {
        const modalResult = await this.modalService.confirm('', this.translationService.getImmediate('walt.confirm-remote-logout'), this.modalActions, true);
        if (modalResult.action === ModalActionType.submit)
            await this.debounceLogout();
    }

    /** Starts a refresh of this walt; ignored while one is already running. */
    refreshWalt() {
        this.waltRefresh.refresh();
    }

    /** Emits the walt's map location so the parent can zoom to it. */
    locateWalt() {
        const location = this.mapLocation();
        if (location) this.locateOutput.emit(location);
    }

    handleClose() {
        this.closeOutput.emit();
    }
}
